import { createContext } from 'react';

import type { ResolvedPreferences } from '../contract/types';
import type { FormatBundle } from '../format/bundle';
import type { CalendarBundle } from '../format/calendar';
import type { PhoneBundle } from '../format/phone';

// Side-effect: start counting evaluations as soon as any public surface is imported, including a
// remote that only consumes the hook and never mounts a provider.
import '../singleton-guard';

/**
 * What the provider publishes to the tree.
 *
 * Both halves are held together in one value so they cannot disagree: the bundle is built from the
 * preferences beside it, and both change at the same instant or not at all. Their shared identity
 * is keyed on `preferences.signature`, which is what makes either safe in a dependency array.
 */
export type LocalizationContextValue = {
  /** Everything the resolver decided, for code that needs the unit rather than a value in it. */
  readonly preferences: ResolvedPreferences;
  /** The formatters, already bound to those preferences. */
  readonly format: FormatBundle;
  /**
   * Calendar arithmetic in the same zone `format` renders in.
   *
   * Beside the formatters rather than derived separately, so a range a feature queries for and the
   * dates it shows can never come from two different calendars.
   */
  readonly calendar: CalendarBundle;
  /**
   * The reader's own phone country, for a field to start on. Beside the formatters so the field and
   * the page behind it agree on whose country it is.
   */
  readonly phone: PhoneBundle;
  /**
   * Whether the rollout flag is on, and so whether `format` converts anything.
   *
   * Published for the one surface that has to omit its answer rather than degrade it: the outbound
   * server parameters, which are left off the request entirely rather than sent unconverted.
   * Nothing that formats a value needs to read it, because the bundle beside it has already been
   * chosen.
   */
  readonly enabled: boolean;
};

/**
 * Deliberately defaults to `undefined` rather than a neutral preference object.
 *
 * A default would let a component that is missing its provider — including one whose federation
 * wiring silently broke — render plausible-looking values in the wrong units. `undefined` turns that
 * into an error at the point of use. See `useLocalizationContext`.
 */
export const LocalizationContext = createContext<
  LocalizationContextValue | undefined
>(undefined);
